import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

type MaybeMutationObserver = typeof MutationObserver | null;

@Injectable({ providedIn: 'root' })
export class NavStateService {
  private collapsedSubject = new BehaviorSubject<boolean>(false);
  collapsed$ = this.collapsedSubject.asObservable();

  constructor() {
    // Initialize from localStorage if present
    try {
      const v = localStorage.getItem('global-nav-collapsed');
      if (v !== null) {
        const b = v === 'true';
        this.collapsedSubject.next(b);
        this.applyClassToWrappers(b);
        this.applyNavWidth(b);
      }
    } catch (e) {
      // ignore storage errors
    }

    // Observe DOM changes so newly inserted .wrapper elements also receive the class
    try {
      const Observer: MaybeMutationObserver = (window as any).MutationObserver || null;
      if (Observer) {
        const observer = new Observer((mutations) => {
          // Re-apply current state when wrappers are added/changed
          const collapsed = this.collapsedSubject.getValue();
          this.applyClassToWrappers(collapsed);
          this.applyNavWidth(collapsed);
        });
        observer.observe(document.documentElement || document.body, { childList: true, subtree: true });
      }
    } catch (e) {
      // ignore environment without DOM
    }
  }

  setCollapsed(collapsed: boolean) {
    try {
      this.collapsedSubject.next(collapsed);
      localStorage.setItem('global-nav-collapsed', String(collapsed));
    } catch (e) {
      // ignore storage errors
    }
    this.applyClassToWrappers(collapsed);
    this.applyNavWidth(collapsed);
  }

  toggle() {
    const next = !this.collapsedSubject.getValue();
    this.setCollapsed(next);
  }

  private applyClassToWrappers(collapsed: boolean) {
    try {
      const wrappers = document.querySelectorAll('.wrapper');
      wrappers.forEach((w) => {
        if (collapsed) {
          w.classList.add('nav-collapsed');
        } else {
          w.classList.remove('nav-collapsed');
        }
      });
    } catch (e) {
      // ignore DOM errors (SSR/testing)
    }
  }

  private applyNavWidth(collapsed: boolean) {
    try {
      if (collapsed) {
        document.documentElement.style.setProperty('--nav-width', '0px');
      } else {
        // remove custom property so CSS fallback (#{$nav-max-width}) applies
        document.documentElement.style.removeProperty('--nav-width');
      }
    } catch (e) {
      // ignore
    }
  }
}
